import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, CalendarDays, Truck, Map as MapIcon, Users, Settings as SettingsIcon,
  Plus, Upload, ClipboardList, Clock, ArrowRight, HardHat, Building2,
} from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from "@/components/ui/command";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import { useCommandPalette, loadRecents, pushRecent } from "../context/CommandPaletteContext";

// ── Navigation catalog ───────────────────────────────────────────────────────
const PAGES = [
  { id: "nav-dashboard", label: "Dashboard", to: "/dashboard", icon: LayoutDashboard,
    keywords: ["home", "overview", "kpis", "summary"] },
  { id: "nav-projects", label: "Jobs", to: "/projects", icon: FolderOpen,
    keywords: ["jobs", "customers", "clients", "accounts", "sites"] },
  { id: "nav-schedule", label: "Schedule", to: "/schedule", icon: CalendarDays,
    keywords: ["calendar", "board", "mobilizations", "mobs", "planner"] },
  { id: "nav-dispatch", label: "Dispatch", to: "/dispatch", icon: Truck,
    keywords: ["lineout", "assign", "route", "daily", "auto dispatch"] },
  { id: "nav-map", label: "Map", to: "/map", icon: MapIcon,
    keywords: ["locations", "geo", "installers on map", "route finder"] },
  { id: "nav-installers", label: "Installers", to: "/installers", icon: Users,
    keywords: ["crew", "workers", "employees", "foremen", "team"],
    hideFor: ["Foreman"] },
];

// ── Quick Actions ────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { id: "qa-new-project", label: "New Job", icon: Plus,
    to: "/projects?new=1", keywords: ["create", "add job", "start job"] },
  { id: "qa-frs-upload", label: "Auto-Build from FRS Upload", icon: Upload,
    to: "/projects?frs=1", keywords: ["import excel", "master frs", "upload frs"] },
  { id: "qa-new-mob", label: "Requests Board", icon: ClipboardList,
    to: "/schedule?view=board", keywords: ["mobilization", "request", "board"] },
  { id: "qa-view-calendar", label: "Open Schedule Calendar", icon: CalendarDays,
    to: "/schedule?view=calendar", keywords: ["month view", "calendar"],
    onSelect: () => { try { localStorage.setItem("fieldops-schedule-view", "calendar"); } catch { /* ignore */ } } },
  { id: "qa-new-installer", label: "New Installer", icon: HardHat,
    to: "/installers?new=1", keywords: ["add worker", "add foreman", "add crew"],
    hideFor: ["Foreman"] },
];

// ── Helpers ─────────────────────────────────────────────────────────────────
const useDebounced = (val, ms = 250) => {
  const [v, setV] = useState(val);
  useEffect(() => { const t = setTimeout(() => setV(val), ms); return () => clearTimeout(t); }, [val, ms]);
  return v;
};

const matches = (haystack, q) => (haystack || "").toLowerCase().includes(q.toLowerCase());

const dateShort = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

// ── Component ────────────────────────────────────────────────────────────────
export default function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const { role } = useRole();

  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 250);

  const [projects, setProjects] = useState([]);
  const [mobs, setMobs] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  const [recents, setRecents] = useState(() => loadRecents());

  // Fetch data ONLY when the palette opens (never blocks page load).
  useEffect(() => {
    if (!open) return;
    setRecents(loadRecents());
    if (loaded) return;
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    Promise.all([
      api.get("/projects", { signal: controller.signal }).catch(() => ({ data: [] })),
      api.get("/mobilizations", { signal: controller.signal }).catch(() => ({ data: [] })),
      api.get("/installers", { signal: controller.signal }).catch(() => ({ data: [] })),
    ]).then(([p, m, i]) => {
      setProjects(p.data || []);
      setMobs(m.data || []);
      setInstallers(i.data || []);
      setLoaded(true);
    }).finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, loaded]);

  // Reset query whenever the dialog closes.
  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const visiblePages = useMemo(
    () => PAGES.filter((p) => !p.hideFor?.includes(role)),
    [role],
  );
  const visibleActions = useMemo(
    () => QUICK_ACTIONS.filter((a) => !a.hideFor?.includes(role)),
    [role],
  );

  const q = debounced.trim();
  const hasQuery = q.length > 0;

  // ── Search results (top matches per entity) ────────────────────────────────
  const matchedProjects = useMemo(() => {
    if (!hasQuery) return [];
    return projects
      .filter((p) =>
        matches(p.project_name, q) ||
        matches(p.job_number, q) ||
        matches(p.city, q) ||
        matches(p.address, q) ||
        matches(p.pm_name, q),
      )
      .slice(0, 6);
  }, [projects, q, hasQuery]);

  const matchedMobs = useMemo(() => {
    if (!hasQuery) return [];
    const projById = Object.fromEntries(projects.map((p) => [p.id, p]));
    return mobs
      .filter((m) => {
        const p = projById[m.project_id] || {};
        return (
          matches(m.title, q) ||
          matches(m.scope_trade, q) ||
          matches(m.status, q) ||
          matches(p.project_name, q) ||
          matches(p.job_number, q)
        );
      })
      .slice(0, 6)
      .map((m) => ({ ...m, _proj: projById[m.project_id] }));
  }, [mobs, projects, q, hasQuery]);

  const matchedInstallers = useMemo(() => {
    if (!hasQuery) return [];
    return installers
      .filter((i) =>
        matches(i.name, q) ||
        matches(i.role, q) ||
        matches(i.home_base, q) ||
        matches(i.email, q),
      )
      .slice(0, 6);
  }, [installers, q, hasQuery]);

  // Selection handlers ────────────────────────────────────────────────────────
  const go = (to) => { setOpen(false); navigate(to); };

  const openProject = (p) => {
    pushRecent({ id: p.id, kind: "project", title: p.project_name || "(Untitled)", subtitle: p.job_number || p.city || "", to: `/projects/${p.id}` });
    go(`/projects/${p.id}`);
  };
  const openMob = (m) => {
    const proj = m._proj || {};
    const to = m.project_id ? `/projects/${m.project_id}?mob=${m.id}` : "/schedule";
    pushRecent({ id: m.id, kind: "mobilization", title: m.title || "Mobilization", subtitle: proj.project_name || m.scope_trade || "", to });
    go(to);
  };
  const openInstaller = (i) => {
    pushRecent({ id: i.id, kind: "installer", title: i.name, subtitle: i.role || "", to: `/installers?focus=${i.id}` });
    go(`/installers?focus=${i.id}`);
  };
  const openRecent = (r) => go(r.to);

  const runQuickAction = (a) => {
    if (typeof a.onSelect === "function") a.onSelect();
    go(a.to);
  };

  const statusPill = (status) => {
    if (!status) return null;
    const c = {
      requested: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
      scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
      dispatched: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
      complete: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    }[status] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
    return <span className={`text-[10px] px-1.5 py-px rounded ${c}`}>{status}</span>;
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      data-testid="command-palette"
    >
      <CommandInput
        placeholder="Search..."
        value={query}
        onValueChange={setQuery}
        data-testid="cmdk-input"
      />
      <CommandList data-testid="cmdk-list">
        <CommandEmpty data-testid="cmdk-empty">
          {loading ? "Loading…" : "No results found."}
        </CommandEmpty>

        {/* Recents (only before typing) */}
        {!hasQuery && recents.length > 0 && (
          <>
            <CommandGroup heading={<span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> Recent</span>}>
              {recents.map((r) => (
                <CommandItem
                  key={`recent-${r.kind}-${r.id}`}
                  value={`recent ${r.title} ${r.subtitle || ""}`}
                  onSelect={() => openRecent(r)}
                  data-testid={`cmdk-item-recent-${r.id}`}
                >
                  <ArrowRight className="w-3.5 h-3.5 mr-2 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{r.title}</div>
                    {r.subtitle && <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{r.subtitle}</div>}
                  </div>
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">{r.kind}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Live search groups (only after typing) */}
        {hasQuery && matchedProjects.length > 0 && (
          <>
            <CommandGroup heading={<span className="flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Jobs</span>}>
              {matchedProjects.map((p) => (
                <CommandItem
                  key={`p-${p.id}`}
                  value={`job ${p.project_name} ${p.job_number || ""} ${p.city || ""} ${p.address || ""}`}
                  onSelect={() => openProject(p)}
                  data-testid={`cmdk-item-project-${p.id}`}
                >
                  <FolderOpen className="w-3.5 h-3.5 mr-2 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{p.project_name || "(Untitled job)"}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {[p.job_number, p.city, p.state].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {p.is_archived && <span className="ml-2 text-[10px] px-1.5 py-px rounded bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300">archived</span>}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {hasQuery && matchedMobs.length > 0 && (
          <>
            <CommandGroup heading={<span className="flex items-center gap-1.5"><ClipboardList className="w-3 h-3" /> Mobilizations</span>}>
              {matchedMobs.map((m) => (
                <CommandItem
                  key={`m-${m.id}`}
                  value={`mob ${m.title || ""} ${m.scope_trade || ""} ${m._proj?.project_name || ""}`}
                  onSelect={() => openMob(m)}
                  data-testid={`cmdk-item-mob-${m.id}`}
                >
                  <ClipboardList className="w-3.5 h-3.5 mr-2 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{m.title || m.scope_trade || "Mobilization"}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {[m._proj?.project_name, m.scope_trade, dateShort(m.scheduled_start_date || m.requested_start_date)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {statusPill(m.status)}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {hasQuery && matchedInstallers.length > 0 && (
          <>
            <CommandGroup heading={<span className="flex items-center gap-1.5"><Users className="w-3 h-3" /> Installers</span>}>
              {matchedInstallers.map((i) => (
                <CommandItem
                  key={`i-${i.id}`}
                  value={`installer ${i.name} ${i.role || ""} ${i.home_base || ""}`}
                  onSelect={() => openInstaller(i)}
                  data-testid={`cmdk-item-installer-${i.id}`}
                >
                  <HardHat className="w-3.5 h-3.5 mr-2 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{i.name}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                      {[i.role, i.home_base].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {i.active === false && <span className="ml-2 text-[10px] px-1.5 py-px rounded bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300">inactive</span>}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Navigation (always visible) */}
        <CommandGroup heading="Go to">
          {visiblePages.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem
                key={p.id}
                value={`page ${p.label} ${(p.keywords || []).join(" ")}`}
                onSelect={() => go(p.to)}
                data-testid={`cmdk-item-${p.id}`}
              >
                <Icon className="w-3.5 h-3.5 mr-2 opacity-60" />
                <span className="flex-1 truncate">{p.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        {/* Quick Actions */}
        <CommandGroup heading="Quick actions">
          {visibleActions.map((a) => {
            const Icon = a.icon;
            return (
              <CommandItem
                key={a.id}
                value={`action ${a.label} ${(a.keywords || []).join(" ")}`}
                onSelect={() => runQuickAction(a)}
                data-testid={`cmdk-item-${a.id}`}
              >
                <Icon className="w-3.5 h-3.5 mr-2 opacity-60" />
                <span className="flex-1 truncate">{a.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
